<template>
  <Teleport to="body">
    <div
      :class="{'idd-backdrop--visible': open}"
      aria-hidden="true"
      class="idd-backdrop"
      @click="open = false"
    />
    <aside
      ref="panel"
      :aria-hidden="!open"
      :class="{'idd-panel--open': open}"
      :inert="!open || undefined"
      aria-labelledby="invoice-design-title"
      aria-modal="true"
      class="idd-panel"
      role="dialog"
      tabindex="-1"
      @keydown="handlePanelKeydown"
    >
      <header class="idd-header">
        <v-icon class="mr-2" color="primary" size="small">mdi-palette-outline</v-icon>
        <span id="invoice-design-title" class="text-subtitle-1 font-weight-medium">Invoice design</span>
        <v-spacer/>
        <v-btn
          aria-label="Close invoice design"
          icon="mdi-close"
          size="small"
          variant="text"
          @click="open = false"
        />
      </header>
      <v-divider/>

      <div
        :aria-busy="loading"
        :class="{'opacity-50': loading}"
        :inert="loading || saving || undefined"
        class="idd-body"
      >
        <InvoiceThemePicker
          v-model="form.theme"
          class="mb-5"
          @apply="applyTheme"
        />

        <v-select
          v-model="form.template_id"
          :items="templateOptions"
          class="mb-3"
          density="compact"
          hide-details
          item-title="label"
          item-value="value"
          label="Template"
          variant="outlined"
          @update:model-value="syncThemeFromDesign"
        />
        <v-select
          v-model="form.font_family"
          :items="fontOptions"
          class="mb-3"
          density="compact"
          hide-details
          item-title="label"
          item-value="value"
          label="Font"
          variant="outlined"
          @update:model-value="syncThemeFromDesign"
        />

        <div class="d-flex ga-2 mb-3">
          <v-text-field v-model="form.primary_color" density="compact" hide-details label="Primary" type="color" variant="outlined" @update:model-value="syncThemeFromDesign"/>
          <v-text-field v-model="form.secondary_color" density="compact" hide-details label="Accent" type="color" variant="outlined" @update:model-value="syncThemeFromDesign"/>
          <v-text-field v-model="form.text_color" density="compact" hide-details label="Text" type="color" variant="outlined" @update:model-value="syncThemeFromDesign"/>
        </div>

        <v-select
          v-model="form.stamp_position"
          :items="stampPositionOptions"
          class="mb-4"
          density="compact"
          hide-details
          item-title="label"
          item-value="value"
          label="Paid stamp position"
          variant="outlined"
        />

        <RouterLink
          :to="{name: 'settings.invoices'}"
          class="text-caption text-primary text-decoration-none"
        >
          More invoice settings →
        </RouterLink>
      </div>

      <v-divider/>
      <footer class="idd-footer">
        <v-btn size="small" variant="text" @click="open = false">{{ $t('cancel') }}</v-btn>
        <v-btn
          :disabled="loading || saving"
          :loading="saving"
          color="primary"
          size="small"
          variant="flat"
          @click="save"
        >
          Save &amp; preview
        </v-btn>
      </footer>
    </aside>
  </Teleport>
</template>

<script lang="ts" setup>
import {nextTick, onBeforeUnmount, reactive, ref, watch} from 'vue';
import axios from 'axios';
import {push} from 'notivue';
import InvoiceThemePicker from '@/components/dashboard/documents/InvoiceThemePicker.vue';
import {findMatchingInvoiceTheme, type InvoiceThemePreset} from '@/config/invoiceThemes';

const props = defineProps<{
  modelValue: boolean;
}>();

const emit = defineEmits<{
  'update:modelValue': [value: boolean];
  saved: [];
}>();

const open = ref(props.modelValue);
watch(() => props.modelValue, (value) => {
  open.value = value;
  if (value) {
    fetchSettings();
  }
});
const loading = ref(false);
const saving = ref(false);
const panel = ref<HTMLElement | null>(null);
let previouslyFocusedElement: HTMLElement | null = null;

const form = reactive<Record<string, string>>({
  theme: 'classic',
  template_id: 'classic',
  font_family: 'sans',
  primary_color: '#1C2A30',
  secondary_color: '#DDE1E3',
  text_color: '#1C2A30',
  stamp_position: 'top-left',
});

const templateOptions = [
  {label: 'Classic', value: 'classic'},
  {label: 'Modern', value: 'modern'},
  {label: 'Friendly', value: 'friendly'},
];
const fontOptions = [
  {label: 'Sans', value: 'sans'},
  {label: 'Humanist', value: 'humanist'},
  {label: 'Serif', value: 'serif'},
];
const stampPositionOptions = [
  {label: 'Top left', value: 'top-left'},
  {label: 'Top center', value: 'top-center'},
  {label: 'Top right', value: 'top-right'},
  {label: 'Center', value: 'center'},
  {label: 'Bottom right', value: 'bottom-right'},
];

const applyTheme = (theme: InvoiceThemePreset): void => {
  form.theme = theme.key;
  form.template_id = theme.template_id;
  form.font_family = theme.font_family;
  form.primary_color = theme.primary_color;
  form.secondary_color = theme.secondary_color;
  form.text_color = theme.text_color;
};

const syncThemeFromDesign = (): void => {
  form.theme = findMatchingInvoiceTheme(form)?.key ?? 'custom';
};

const setBackgroundInert = (value: boolean): void => {
  document.querySelector<HTMLElement>('#app')?.toggleAttribute('inert', value);
};

const focusPanel = async (): Promise<void> => {
  await nextTick();
  panel.value?.focus();
};

const handlePanelKeydown = (event: KeyboardEvent): void => {
  if (event.key === 'Escape') {
    event.preventDefault();
    open.value = false;
    return;
  }

  if (event.key !== 'Tab' || !panel.value) {
    return;
  }

  const focusableElements = Array.from(panel.value.querySelectorAll<HTMLElement>(
    'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])',
  )).filter((element) => !element.hasAttribute('inert') && element.offsetParent !== null);

  if (focusableElements.length === 0) {
    event.preventDefault();
    panel.value.focus();
    return;
  }

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];
  if (document.activeElement === panel.value) {
    event.preventDefault();
    (event.shiftKey ? lastElement : firstElement).focus();
  } else if (event.shiftKey && document.activeElement === firstElement) {
    event.preventDefault();
    lastElement.focus();
  } else if (!event.shiftKey && document.activeElement === lastElement) {
    event.preventDefault();
    firstElement.focus();
  }
};

async function fetchSettings(): Promise<void> {
  loading.value = true;
  try {
    const {data} = await axios.get('/v1/invoice-settings');
    Object.keys(form).forEach((key) => {
      if (data.settings?.[key] !== undefined) {
        form[key] = data.settings[key];
      }
    });
    syncThemeFromDesign();
  } catch {
    push.error({message: 'Failed to load invoice design'});
  } finally {
    loading.value = false;
  }
}

async function save(): Promise<void> {
  if (loading.value || saving.value) {
    return;
  }

  saving.value = true;
  try {
    await axios.put('/v1/invoice-settings', {...form});
    push.success({message: 'Invoice design updated'});
    emit('saved');
    open.value = false;
  } catch {
    push.error({message: 'Failed to save invoice design'});
  } finally {
    saving.value = false;
  }
}

watch(open, async (value) => {
  emit('update:modelValue', value);

  if (value) {
    previouslyFocusedElement = document.activeElement instanceof HTMLElement
      ? document.activeElement
      : null;
    setBackgroundInert(true);
    await focusPanel();
  } else {
    setBackgroundInert(false);
    previouslyFocusedElement?.focus();
    previouslyFocusedElement = null;
  }
});

onBeforeUnmount(() => {
  setBackgroundInert(false);
});
</script>

<!-- Not scoped: the panel is teleported to <body>, outside this component's DOM
     scope, so scoped styles wouldn't reach it. A hand-rolled fixed panel pinned
     to the right edge (like the What's New sidebar) — position:fixed resolves
     against the viewport, and Teleport keeps any ancestor transform from
     trapping it in the controls card. -->
<style>
.idd-panel {
  position: fixed;
  top: 0;
  right: 0;
  height: 100vh;
  width: 420px;
  max-width: 92vw;
  display: flex;
  flex-direction: column;
  background: rgb(var(--v-theme-surface));
  box-shadow: -8px 0 28px rgba(0, 0, 0, 0.18);
  transform: translateX(100%);
  visibility: hidden;
  transition: transform 0.25s ease,
  visibility 0s linear 0.25s;
  z-index: 1999;
}

.idd-panel--open {
  transform: translateX(0);
  visibility: visible;
  transition-delay: 0s;
}

.idd-header {
  display: flex;
  align-items: center;
  padding: 12px 16px;
}

.idd-body {
  flex: 1 1 auto;
  overflow-y: auto;
  padding: 16px;
}

.idd-footer {
  display: flex;
  justify-content: flex-end;
  gap: 8px;
  padding: 12px 16px;
}

.idd-backdrop {
  position: fixed;
  inset: 0;
  background: rgba(0, 0, 0, 0.4);
  opacity: 0;
  pointer-events: none;
  transition: opacity 0.25s ease;
  z-index: 1998;
}

.idd-backdrop--visible {
  opacity: 1;
  pointer-events: auto;
}
</style>
